/**
 * Template Sharing Service
 *
 * Enables private sponsor-to-sponsor template sharing via email.
 * Flow: Share → Notify → Preview → Accept/Decline
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  query,
  where,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { db } from './config';
import type {
  TemplateShare,
  TemplateNotification,
  HomeworkTemplate,
} from '../../types/stepWork';

// ============================================
// Constants
// ============================================

const SHARE_EXPIRATION_DAYS = 30;

// ============================================
// Helper Functions
// ============================================

/**
 * Find user by email in user profiles
 */
async function findUserByEmail(email: string): Promise<string | null> {
  try {
    const usersRef = collection(db, 'users');
    const usersSnapshot = await getDocs(usersRef);

    for (const userDoc of usersSnapshot.docs) {
      const profile = userDoc.data();
      if (profile.email === email) {
        return userDoc.id;
      }
    }

    return null;
  } catch (error) {
    console.error('Error finding user by email:', error);
    return null;
  }
}

/**
 * Create notification for template share
 */
async function createNotification(
  userId: string,
  shareId: string,
  templateName: string,
  fromUserName: string,
  fellowship: string
): Promise<void> {
  try {
    const notificationRef = doc(collection(db, 'users', userId, 'notifications'));
    const notification: Omit<TemplateNotification, 'id'> = {
      type: 'template_share',
      shareId,
      templateName,
      fromUserName,
      fellowship: fellowship as any,
      read: false,
      createdAt: serverTimestamp() as Timestamp,
    };

    await setDoc(notificationRef, notification);
    console.log('✅ Notification created for user:', userId);
  } catch (error) {
    console.error('Error creating notification:', error);
    throw error;
  }
}

/**
 * Mark notification as read
 */
async function markNotificationRead(userId: string, shareId: string): Promise<void> {
  try {
    const notificationsRef = collection(db, 'users', userId, 'notifications');
    const q = query(notificationsRef, where('shareId', '==', shareId));
    const snapshot = await getDocs(q);

    if (!snapshot.empty) {
      const notificationDoc = snapshot.docs[0];
      await updateDoc(notificationDoc.ref, { read: true });
      console.log('✅ Notification marked as read');
    }
  } catch (error) {
    console.error('Error marking notification as read:', error);
  }
}

// ============================================
// Main Service Functions
// ============================================

/**
 * Share a template with another sponsor via email
 */
export async function shareTemplate(
  templateId: string,
  toEmail: string,
  fromUserId: string,
  fromUserName: string
): Promise<{ success: boolean; shareId?: string; error?: string }> {
  try {
    console.log('📤 Sharing template:', { templateId, toEmail, fromUserId });

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(toEmail)) {
      return { success: false, error: 'Invalid email format' };
    }

    // Fetch template to validate and get metadata
    const templateRef = doc(db, 'users', fromUserId, 'homeworkTemplates', templateId);
    const templateSnap = await getDoc(templateRef);

    if (!templateSnap.exists()) {
      return { success: false, error: 'Template not found' };
    }

    const template = { id: templateSnap.id, ...templateSnap.data() } as HomeworkTemplate;

    // Validate ownership
    if (template.createdBy !== fromUserId) {
      return { success: false, error: 'You can only share templates you created' };
    }

    // Look up recipient user by email
    const toUserId = await findUserByEmail(toEmail);

    // Calculate expiration (30 days from now)
    const now = new Date();
    const expiresAt = new Date(now.getTime() + SHARE_EXPIRATION_DAYS * 24 * 60 * 60 * 1000);

    // Create share document
    const shareRef = doc(collection(db, 'templateShares'));
    const share: Omit<TemplateShare, 'id'> = {
      templateId,
      templateName: template.name,
      fellowship: template.fellowship,

      fromUserId,
      fromUserName,

      toEmail: toEmail.toLowerCase(),
      toUserId: toUserId || undefined,

      status: 'pending',

      createdAt: serverTimestamp() as Timestamp,
      expiresAt: Timestamp.fromDate(expiresAt),
    };

    await setDoc(shareRef, share);
    console.log('✅ Template share created:', shareRef.id);

    // Create notification if user found
    if (toUserId) {
      await createNotification(
        toUserId,
        shareRef.id,
        template.name,
        fromUserName,
        template.fellowship
      );
    }

    return { success: true, shareId: shareRef.id };
  } catch (error) {
    console.error('❌ Error sharing template:', error);
    return { success: false, error: 'Failed to share template. Please try again.' };
  }
}

/**
 * Accept a template share and copy to recipient's library
 */
export async function acceptShare(
  shareId: string,
  toUserId: string
): Promise<{ success: boolean; templateId?: string; error?: string }> {
  try {
    console.log('✅ Accepting share:', { shareId, toUserId });

    // Fetch share
    const shareRef = doc(db, 'templateShares', shareId);
    const shareSnap = await getDoc(shareRef);

    if (!shareSnap.exists()) {
      return { success: false, error: 'Share not found' };
    }

    const share = { id: shareSnap.id, ...shareSnap.data() } as TemplateShare;

    // Validate recipient
    if (share.toUserId !== toUserId) {
      return { success: false, error: 'This share is not for you' };
    }

    // Validate status
    if (share.status !== 'pending') {
      return { success: false, error: `Share already ${share.status}` };
    }

    // Check expiration
    const now = new Date();
    if (share.expiresAt.toDate() < now) {
      await updateDoc(shareRef, { status: 'expired' });
      return { success: false, error: 'Share has expired' };
    }

    // Fetch original template
    const originalTemplateRef = doc(
      db,
      'users',
      share.fromUserId,
      'homeworkTemplates',
      share.templateId
    );
    const originalTemplateSnap = await getDoc(originalTemplateRef);

    if (!originalTemplateSnap.exists()) {
      return { success: false, error: 'Original template no longer exists' };
    }

    const originalTemplate = {
      id: originalTemplateSnap.id,
      ...originalTemplateSnap.data(),
    } as HomeworkTemplate;

    // Copy template to recipient's library
    const newTemplateRef = doc(collection(db, 'users', toUserId, 'homeworkTemplates'));
    const newTemplate: Omit<HomeworkTemplate, 'id'> = {
      ...originalTemplate,
      createdBy: toUserId, // New owner
      isDefault: false,    // Don't auto-apply
      appliedToCount: 0,   // Reset usage
      sharedFrom: {
        originalCreatorId: share.fromUserId,
        originalCreatorName: share.fromUserName,
        sharedAt: serverTimestamp() as Timestamp,
      },
      createdAt: serverTimestamp() as Timestamp,
      lastUpdatedAt: serverTimestamp() as Timestamp,
    };

    await setDoc(newTemplateRef, newTemplate);
    console.log('✅ Template copied to recipient:', newTemplateRef.id);

    // Update share status
    await updateDoc(shareRef, {
      status: 'accepted',
      respondedAt: serverTimestamp(),
    });

    // Mark notification as read
    await markNotificationRead(toUserId, shareId);

    return { success: true, templateId: newTemplateRef.id };
  } catch (error) {
    console.error('❌ Error accepting share:', error);
    return { success: false, error: 'Failed to accept template. Please try again.' };
  }
}

/**
 * Decline a template share
 */
export async function declineShare(
  shareId: string,
  toUserId: string
): Promise<{ success: boolean; error?: string }> {
  try {
    console.log('❌ Declining share:', { shareId, toUserId });

    // Fetch share
    const shareRef = doc(db, 'templateShares', shareId);
    const shareSnap = await getDoc(shareRef);

    if (!shareSnap.exists()) {
      return { success: false, error: 'Share not found' };
    }

    const share = { id: shareSnap.id, ...shareSnap.data() } as TemplateShare;

    // Validate recipient
    if (share.toUserId !== toUserId) {
      return { success: false, error: 'This share is not for you' };
    }

    // Validate status
    if (share.status !== 'pending') {
      return { success: false, error: `Share already ${share.status}` };
    }

    // Update share status
    await updateDoc(shareRef, {
      status: 'declined',
      respondedAt: serverTimestamp(),
    });

    // Mark notification as read
    await markNotificationRead(toUserId, shareId);

    console.log('✅ Share declined');
    return { success: true };
  } catch (error) {
    console.error('❌ Error declining share:', error);
    return { success: false, error: 'Failed to decline share. Please try again.' };
  }
}

/**
 * Get all shares sent by a user (for share history)
 */
export async function getMyShares(userId: string): Promise<TemplateShare[]> {
  try {
    const sharesRef = collection(db, 'templateShares');
    const q = query(sharesRef, where('fromUserId', '==', userId));
    const snapshot = await getDocs(q);

    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    })) as TemplateShare[];
  } catch (error) {
    console.error('Error fetching my shares:', error);
    return [];
  }
}

/**
 * Get pending shares for a user
 */
export async function getPendingShares(userId: string): Promise<TemplateShare[]> {
  try {
    const sharesRef = collection(db, 'templateShares');
    const q = query(
      sharesRef,
      where('toUserId', '==', userId),
      where('status', '==', 'pending')
    );
    const snapshot = await getDocs(q);

    // Filter out expired shares
    const now = new Date();
    const shares = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    })) as TemplateShare[];

    return shares.filter(share => share.expiresAt.toDate() >= now);
  } catch (error) {
    console.error('Error fetching pending shares:', error);
    return [];
  }
}

/**
 * Check for pending shares on login and create notifications
 * Called automatically when user logs in
 */
export async function checkPendingSharesOnLogin(
  userId: string,
  userEmail: string
): Promise<void> {
  try {
    console.log('🔍 Checking pending shares for:', userEmail);

    const sharesRef = collection(db, 'templateShares');
    const q = query(
      sharesRef,
      where('toEmail', '==', userEmail.toLowerCase()),
      where('toUserId', '==', null)
    );
    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      console.log('No pending shares found');
      return;
    }

    console.log(`Found ${snapshot.docs.length} pending shares`);

    // Update each share and create notifications
    for (const shareDoc of snapshot.docs) {
      const share = { id: shareDoc.id, ...shareDoc.data() } as TemplateShare;

      // Update toUserId
      await updateDoc(shareDoc.ref, { toUserId: userId });

      // Create notification
      await createNotification(
        userId,
        shareDoc.id,
        share.templateName,
        share.fromUserName,
        share.fellowship
      );
    }

    console.log('✅ Pending shares matched and notifications created');
  } catch (error) {
    console.error('❌ Error checking pending shares:', error);
  }
}

/**
 * Get all notifications for a user
 */
export async function getNotifications(userId: string): Promise<TemplateNotification[]> {
  try {
    const notificationsRef = collection(db, 'users', userId, 'notifications');
    const snapshot = await getDocs(notificationsRef);

    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    })) as TemplateNotification[];
  } catch (error) {
    console.error('Error fetching notifications:', error);
    return [];
  }
}

/**
 * Get unread notification count
 */
export async function getUnreadNotificationCount(userId: string): Promise<number> {
  try {
    const notificationsRef = collection(db, 'users', userId, 'notifications');
    const q = query(notificationsRef, where('read', '==', false));
    const snapshot = await getDocs(q);

    return snapshot.size;
  } catch (error) {
    console.error('Error fetching unread count:', error);
    return 0;
  }
}
